import { Component, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, merge, Observable, of } from 'rxjs';
import { catchError, filter, switchMap, take, tap, map, delay } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import { IAuthState } from '../../services/auth.service.interface';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { IInvestmentYear, IPropertyEntity, IPropertyYear, PropertyService } from 'src/app/services/poperty.service';
import { DateUtil } from 'src/app/lib/date-util';
import { StringUtil } from 'src/app/lib/string-util';
import { IPropertyChartViewModel, PropertyPageComponent } from '../property-page/property-page.component';
import { FormSelectComponent, IFormSelectChangedArg, IFormSelectViewModel } from 'src/app/forms/form-select/form-select.component';
import { IOption } from 'ng-uikit-pro-standard';
import { Router } from '@angular/router';
import { IPageLoaderViewModel } from 'src/app/components/page-loader/page-loader.component';
import { environment } from 'src/environments/environment';


@Component({
  selector: 'app-home',
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.scss']
})
export class HomePageComponent implements OnInit {

  /**
   * 
   * @param authService 
   * @param _http 
   * @param propertyService 
   */
  constructor(private authService: AuthService, private _http: HttpClient, private propertyService: PropertyService, private router: Router) {
  }


  pageVm$: Observable<IPageViewModel>;

  pageViewModel: any = {
    isSimple: true
  }

  /**
   * 
   */
  ngOnInit() {
    this.pageVm$ = this.authService.watchState$().pipe(
      switchMap(authState => {
        
        if (authState.workingText) {
          return of({ workingText: authState.workingText, isLoggedIn: authState.isLoggedIn, properties: [] });

        } else if (authState.isLoggedIn) {
          const pl$ = this.propertyService.propertyList().pipe(
            map(properties => {
              const ret: IPageViewModel = {
                isLoggedIn: authState.isLoggedIn,
                properties: properties,
                dashboardVm: properties.length ? this.dashboardVm(properties, 10) : undefined,
                showWelcome: properties.length == 0,
                allowSamples: properties.length == 0 && environment.sampleProperties?.length > 0
              };
              return ret;
            })
          );
          return merge(pl$, of({ workingText: 'Loading...', isLoggedIn: authState.isLoggedIn, properties: [] }));
        
        } else {
          const ret: IPageViewModel = {
            isLoggedIn: false,
            properties: [],
            dashboardVm: undefined,
            showWelcome: false
          };
          return of(ret);
        }
      }),
      catchError(err => {
        const ret: IPageViewModel = {
          isLoggedIn: false,
          properties: [],
          errorText: err
        };
        return of(ret);
      })
    );
  }

  /**
   * 
   * @param vm 
   * @param $event 
   */
  sliderChanged(vm: IPageViewModel, event: { value: number }) {
    vm.dashboardVm = this.dashboardVm(vm.properties, event.value, vm.dashboardVm.propertyListVm.data.viewMode);
  }

  //#region dashboard widgets

  /**
   * @param properties
   * @param forecastYears
   * @param viewMode
   */
  private dashboardVm(properties: IPropertyEntity[], forecastYears: number, viewMode: string = 'active'): IDashboardViewModel {

    const activeProperties = properties.filter(p => !p.inactive);
    const inactiveProperties = properties.filter(p => p.inactive === true);

    const minCalendarYear = DateUtil.now().getFullYear();
    const maxCalendarYear = minCalendarYear + forecastYears;

    const totalEquity = activeProperties.reduce((pv, cv) => pv + cv.investmentYears.filter(iy => iy.calendarYear < maxCalendarYear).reverse()[0]?.runningTotals?.equity ?? 0, 0);

    let totalCashflow = 0;
    activeProperties.forEach(i => i.investmentYears.filter(iy => iy.calendarYear < maxCalendarYear).forEach(y => totalCashflow += y.endOfYearTotals.estimated.cashFlow));

    let totalPaydown = 0;
    activeProperties.forEach(i => i.investmentYears.filter(iy => iy.calendarYear < maxCalendarYear).forEach(y => totalPaydown += y.endOfYearTotals.estimated.tenantPaydown));

    // dashboard view model
    const ret: IDashboardViewModel = {

      propertyListVm: this.propertyListVm(properties, minCalendarYear, maxCalendarYear, viewMode),

      years: forecastYears,
      sliderVm: {
        min: 1,
        max: 35,
        current: 10
      },

      cards: [
        { title: 'Properties', value: `${activeProperties.length}`, helpText: 'Number of properties in portfolio' },
        { title: 'Hold Period', value: `${forecastYears} year(s)`, helpText: 'Years the properties are intended to be held' },
        { title: 'Equity', value: StringUtil.toCurrency(totalEquity), helpText: 'Aggregate appreciation and debt paydown for the hold period' },
        { title: 'Cashflow', value: StringUtil.toCurrency(totalCashflow), helpText: 'Aggregate cashflow for the hold period' },
      ],

      charts: [
        PropertyPageComponent.propertyCashflowChartVm('Cashflow', activeProperties, forecastYears),
        PropertyPageComponent.propertyEquityChartVm('Equity', activeProperties, forecastYears)
      ]
    };
    return ret;
  }

  //#region property list

  /**
   * 
   * @param vm 
   * @param pvm 
   * @param checked
   */
  async includeChanged(vm: IPageViewModel, pvm: IPropertyViewModel, checked: boolean) {
    pvm.data.inactive = checked ? false : true;

    try {
      pvm.displayVm.workingText = 'Saving...';
      pvm.displayVm.errorText = '';

      var result = await of({}).pipe(
        delay(500),
        switchMap(() => this.propertyService.propertySave(pvm.data))
      ).toPromise();
  
      vm.dashboardVm = this.dashboardVm(vm.properties, vm.dashboardVm.years, vm.dashboardVm.propertyListVm.data.viewMode);

    } catch (err) {
      pvm.displayVm.errorText = err;      
    }
  }

  /**
   * 
   * @param properties 
   * @param minCalendarYear 
   * @param maxCalendarYear 
   * @returns 
   */
  private propertyListVm(properties: IPropertyEntity[], minCalendarYear: number, maxCalendarYear: number, viewMode: string = 'active'): IPropertyListViewModel {
    const data = {
      viewMode: viewMode
    };

    const viewOptions: IOption[] = [
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' }
    ];

    const ret: IPropertyListViewModel = {
      data: data,
      title: 'My Portfolio',
      actions: [
        { code: 'property-add', name: 'Add Property' }
      ],

      viewOptions: FormSelectComponent.viewModel(data, 'viewMode', { title: undefined, options: viewOptions, placeholder: 'Filter View' }),

      propertyVms: properties.map(p => {

        try {
          const ownedYears = p.investmentYears.filter(iy => iy.calendarYear < minCalendarYear);
          const forecastYears = p.investmentYears.filter(iy => iy.calendarYear < maxCalendarYear);
          const investmentYear = forecastYears.reverse()[0];
          
          const pvm: IPropertyViewModel = {
            data: p,
            displayVm: {
              code: p.code,
              name: p.propertyInfo.name,
              address: p.propertyInfo.address,
              pictureUrl: this.propertyService.imageUrlPrimary(p),
  
              details: [
                { title: 'Purchased On', value: `${p.propertyInfo.purchaseDate}` },
                { title: 'Purchased At', value: StringUtil.toCurrency(p.propertyInfo.purchasePrice) },
                { title: 'Hold Period', value: `${forecastYears.length} year(s)` },
                { title: 'Cashflow', value: StringUtil.toCurrency(investmentYear.runningTotals.cashflow) },
                { title: 'Appreciation', value: StringUtil.toCurrency(investmentYear.runningTotals.appreciation) },
                { title: 'Paydown', value: StringUtil.toCurrency(investmentYear.runningTotals.tenantPaydown) },
                { title: 'Equity', value: StringUtil.toCurrency(investmentYear.runningTotals.equity) }
              ],
              
              include: p.inactive ? false : true
            }          
          };  
          return pvm;
  
        } catch (err) {
          const pvm: IPropertyViewModel = {
            data: p,
            displayVm: undefined,
            errorText: `Error: '${err}' for property '${p.code}'`
          };
          return pvm;
        }

      })
    };
    return ret;
  }

  /**
   * 
   * @param vm 
   * @param arg 
   */
  propertyListViewChanged(vm: IPageViewModel, arg: IFormSelectChangedArg) {
    vm.dashboardVm = this.dashboardVm(vm.properties, vm.dashboardVm.years, arg.option.value);
  }

  /**
 * 
 * @param vm 
 * @param code 
 */
  propertyListActionStart(vm: IPageViewModel, code: string) {
    if (code == 'property-add')
      this.router.navigate(['properties', 'new-property']);
    if (code == 'load-samples')
      this.loadSamples(vm);
  }

  /**
   * 
   * @param vm 
   */
  private async loadSamples(vm: IPageViewModel) {
    
    vm.workingText = 'Loading Samples';
    vm.dashboardVm = undefined;
    vm.showWelcome = false;

    try {
      const properties = await of({}).pipe(
        delay(100),
        switchMap(() => this.propertyService.loadSamples())
      ).toPromise();

      vm.workingText = undefined;
      vm.properties = [
        ...properties
      ];
      vm.dashboardVm = this.dashboardVm(properties, 10);
      
    } catch (err) {
      vm.errorText = err;
    } finally {
      
    }
  }

  //#endregion property list

  chartVm_clicked(vm: IPageViewModel, cvm: IPropertyChartViewModel, event: any): void {
  }

  chartVm_hovered(vm: IPageViewModel, cvm: IPropertyChartViewModel, event: any): void {
  }


  //#endregion dashboard widgets

}

/**
 * 
 */
interface IPageViewModel extends IPageLoaderViewModel {
  isLoggedIn: boolean;
  
  properties: IPropertyEntity[];

  dashboardVm?: IDashboardViewModel;

  showWelcome?: boolean;
  allowSamples?: boolean;
}


/**
 * 
 */
interface IDashboardViewModel {  
  years: number;

  sliderVm: {
    min: number;
    max: number;
    current: number
  }

  propertyListVm: IPropertyListViewModel;

  cards: { title: string, value: string, helpText?: string }[];
  charts: IPropertyChartViewModel[];
}

/**
 * 
 */
interface IPropertyListViewModel {
  data: {
    viewMode: string
  };

  title: string;

  actions: { code: string, name: string }[];
  viewOptions: IFormSelectViewModel;

  propertyVms: IPropertyViewModel[];
}

/**
 * 
 */
interface IPropertyViewModel {
  data: IPropertyEntity;

  errorText?: string;

  displayVm: {
    code: string;
    name: string;
    address: string;
    pictureUrl: string;

    details: { title: string, value: string }[];

    include: boolean;

    workingText?: string;
    errorText?: string;
  };
}
